import { useState, useCallback, useRef } from 'react';

/**
 * useToast - Custom hook for managing toast notifications
 * 
 * Replaces the duplicated pattern across 19+ files:
 * const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
 * const showToast = (message, type) => { ... setTimeout... };
 * 
 * @param {number} duration - Auto-dismiss duration in ms (default: 3000)
 * @returns {{ toast: { show: boolean, message: string, type: string }, showToast: Function }}
 */
export const useToast = (duration = 3000) => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const timeoutRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Show the toast
    setToast({ show: true, message, type });
    
    // Auto-dismiss after duration
    timeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, duration);
  }, [duration]);

  const hideToast = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setToast({ show: false, message: '', type: 'success' });
  }, []);

  return { toast, showToast, hideToast };
};

export default useToast;

